import { useEffect, useRef } from 'react';
import p5 from 'p5';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

export const ParticleSystem = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const particles = useRef<Particle[]>([]);
  const sketch = useRef<p5>();
  const mousePos = useRef({ x: 0, y: 0 });
  const isMouseOver = useRef(false);

  useEffect(() => {
    if (!containerRef.current) return;

    const s = new p5((p: p5) => {
      p.setup = () => {
        const canvas = p.createCanvas(
          containerRef.current!.offsetWidth,
          containerRef.current!.offsetHeight
        );
        canvas.parent(containerRef.current!);
        canvas.style('display', 'block');
        canvas.style('position', 'absolute');
        canvas.style('top', '0');
        canvas.style('left', '0');
        canvas.style('z-index', '0');
        canvas.style('pointer-events', 'none');
        
        
        for (let i = 0; i < 50; i++) {
          particles.current.push({
            x: p.random(p.width),
            y: p.random(p.height),
            vx: p.random(-0.01, 0.01), // 
            vy: p.random(-0.01, 0.01)  // 
          });
        }
      };

      p.draw = () => {
        p.clear();
        
        for (let i = 0; i < particles.current.length; i++) {
          const particle = particles.current[i];
          
          if (isMouseOver.current) {
            const dx = mousePos.current.x - particle.x;
            const dy = mousePos.current.y - particle.y;
            const dist = p.sqrt(dx * dx + dy * dy);
            
            if (dist < 150) {
              const force = 0.005; // 
              particle.vx += (dx / dist) * force;
              particle.vy += (dy / dist) * force;
            }
          }
          
          // Update position
          particle.x += particle.vx;
          particle.y += particle.vy;
          
          // Add friction
          particle.vx *= 0.995;
          particle.vy *= 0.995;
          
          // Keep particles within bounds
          if (particle.x < 0) particle.x = p.width;
          if (particle.x > p.width) particle.x = 0;
          if (particle.y < 0) particle.y = p.height;
          if (particle.y > p.height) particle.y = 0;
          
          // Draw connections
          p.stroke(255, 50);
          p.strokeWeight(1);
          for (let j = i + 1; j < particles.current.length; j++) {
            const other = particles.current[j];
            const d = p.dist(particle.x, particle.y, other.x, other.y);
            if (d < 100) {
              p.line(particle.x, particle.y, other.x, other.y);
            }
          }
          
          // Draw particle
          p.noStroke();
          p.fill(255, 200);
          p.ellipse(particle.x, particle.y, 3, 3);
        }
      };

      p.windowResized = () => {
        if (!containerRef.current) return;
        p.resizeCanvas(
          containerRef.current.offsetWidth,
          containerRef.current.offsetHeight
        );
      };
    });

    sketch.current = s;

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      mousePos.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
    };

    const handleMouseEnter = () => {
      isMouseOver.current = true;
    };

    const handleMouseLeave = () => {
      isMouseOver.current = false;
    };

    containerRef.current.addEventListener('mousemove', handleMouseMove);
    containerRef.current.addEventListener('mouseenter', handleMouseEnter);
    containerRef.current.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      if (containerRef.current) {
        containerRef.current.removeEventListener('mousemove', handleMouseMove);
        containerRef.current.removeEventListener('mouseenter', handleMouseEnter);
        containerRef.current.removeEventListener('mouseleave', handleMouseLeave);
      }
      s.remove();
    };
  }, []);

  return <div ref={containerRef} className="w-full h-full" />;
};
